import { supabase } from '../config/supabaseClient.js';
import { loader } from '../ui/loader.js';
import { el, clear, formatarNome, mascararCpf, mascararMatricula, rotuloDocumento, empresaCurta, fmtDataHora } from '../lib/dom.js';
import { orBuscaPessoa, orBuscaPessoaLegado, marcarHomonimos } from '../lib/db.js';
import { buscaSelect } from '../ui/buscaSelect.js';
import { renderTabela } from '../ui/table.js';
import { abrirModal } from '../ui/modal.js';
import { toast } from '../lib/toast.js';
import { operadorAtual } from '../lib/operador.js';
import { assinarTabela, pollPeriodico } from '../lib/realtime.js';

const TETO_TODOS = 500;

/** Inicio do dia atual (local) em ISO — para mostrar so "ate virar o dia". */
function inicioDoDiaISO() { const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString(); }

async function buscarColaboradores(termo) {
  const filtros = termo ? [orBuscaPessoa(termo), orBuscaPessoaLegado(termo)] : [''];
  const sels = ['id, nome, cpf, matricula, empregador:empregador_id ( empresa )', 'id, nome, cpf, matricula'];
  let data = null, error = true;
  for (const filtro of filtros) {
    for (const sel of sels) {
      let q = supabase.from('colaborador').select(sel).eq('status', 'ATIVO');
      if (filtro) q = q.or(filtro);
      ({ data, error } = await q.order('nome').limit(30));
      if (!error) break;
    }
    if (!error) break;
  }
  marcarHomonimos(data || []);
  return (data || []).map((c) => ({
    value: c.id,
    label: `${c.homonimo ? '⚠ ' : ''}${formatarNome(c.nome)}${c.empregador?.empresa ? ' · ' + empresaCurta(c.empregador.empresa) : ''} · ${rotuloDocumento(c)}`,
  }));
}

function mapear(linhas) {
  return (linhas || []).map((r) => ({
    nome: r.colaborador?.nome || '',
    matricula: r.colaborador?.matricula || '',
    cpf: r.colaborador?.cpf || '',
    operador: r.operador_nome || '',
    registrado_em: r.registrado_em,
  }));
}

async function listarHoje() {
  const base = (sel) => supabase.from('cracha_esquecido').select(sel)
    .gte('registrado_em', inicioDoDiaISO()).order('registrado_em', { ascending: false });
  let resp = await base('id, registrado_em, operador_nome, colaborador:colaborador_id(nome, matricula, cpf)');
  if (resp.error) resp = await base('id, registrado_em, colaborador:colaborador_id(nome, matricula, cpf)');
  if (resp.error) { console.error('[cracha listarHoje]', resp.error.message); return []; }
  return mapear(resp.data);
}

/** Historico (log que cresce): traz os mais recentes ate o teto e avisa se cortar. */
async function listarTodos() {
  const base = (sel) => supabase.from('cracha_esquecido').select(sel)
    .order('registrado_em', { ascending: false }).limit(TETO_TODOS);
  let resp = await base('id, registrado_em, operador_nome, colaborador:colaborador_id(nome, matricula, cpf)');
  if (resp.error) resp = await base('id, registrado_em, colaborador:colaborador_id(nome, matricula, cpf)');
  if (resp.error) { console.error('[cracha listarTodos]', resp.error.message); return []; }
  return mapear(resp.data);
}

export const paginaCracha = {
  async render(container) {
    container.innerHTML = '';
    container.append(
      el('h1', { class: 'view__titulo', text: 'Crachá' }),
      el('p', { class: 'view__sub', text: 'Colaborador que esqueceu o crachá. Clique em "Registrar" e busque por nome, CPF ou matrícula.' }),
    );

    const btn = el('button', { class: 'btn btn--primario', onClick: abrirRegistro },
      [el('i', { class: 'ph ph-identification-badge' }), ' Registrar esqueceu o crachá']);

    let aba = 'hoje';
    const abaHoje = el('button', { class: 'aba aba--ativa', onClick: () => trocar('hoje') }, 'Esqueceram hoje');
    const abaTodos = el('button', { class: 'aba', onClick: () => trocar('todos') }, 'Todos os registros');
    const listaBox = el('div');
    container.append(
      el('div', { style: 'margin-bottom:18px' }, [btn]),
      el('div', { class: 'abas', style: 'margin-bottom:12px' }, [abaHoje, abaTodos]),
      listaBox,
    );

    function trocar(a) {
      aba = a;
      abaHoje.classList.toggle('aba--ativa', a === 'hoje');
      abaTodos.classList.toggle('aba--ativa', a === 'todos');
      desenhar(true);
    }

    function abrirRegistro() {
      const picker = buscaSelect({ buscar: buscarColaboradores });
      abrirModal({
        titulo: 'Registrar esqueceu o crachá', classe: 'modal--larga',
        corpo: [
          el('p', { class: 'view__sub', style: 'margin-top:0', text: 'Busque o colaborador por nome, CPF ou matrícula.' }),
          el('div', { class: 'campo' }, [el('label', { text: 'Colaborador' }), picker.elemento]),
        ],
        acoes: [
          { rotulo: 'Cancelar', classe: 'btn' },
          { rotulo: 'Registrar', classe: 'btn btn--primario', onClick: async () => {
            const id = picker.getValue();
            if (!id) { toast('Selecione o colaborador.', 'erro'); return false; }
            const { data: jah } = await supabase.from('cracha_esquecido')
              .select('id').eq('colaborador_id', id).gte('registrado_em', inicioDoDiaISO()).limit(1);
            if (jah && jah.length) { toast('Esse colaborador já foi registrado hoje.', 'info'); return false; }
            const { error } = await supabase.from('cracha_esquecido')
              .insert({ colaborador_id: id, operador_usuario_id: operadorAtual.id || null, operador_nome: operadorAtual.nome || null });
            if (error) { toast('Erro ao registrar: ' + error.message, 'erro'); return false; }
            toast('Crachá esquecido registrado.', 'sucesso');
            desenhar();
            return true;
          } },
        ],
      });
    }

    function colunas(comData) {
      return [
        { chave: 'nome', rotulo: 'Colaborador', render: (l) => formatarNome(l.nome) },
        { chave: 'matricula', rotulo: 'Matrícula', ordenavel: false, render: (l) => mascararMatricula(l.matricula) },
        { chave: 'cpf', rotulo: 'CPF', ordenavel: false, render: (l) => mascararCpf(l.cpf) },
        { chave: 'registrado_em', rotulo: comData ? 'Data/hora' : 'Hora', render: (l) => fmtDataHora(l.registrado_em) },
        { chave: 'operador', rotulo: 'Registrado por', ordenavel: false, render: (l) => formatarNome(l.operador) },
      ];
    }

    async function desenhar(primeira = false) {
      if (primeira) { clear(listaBox); listaBox.append(loader()); }
      const linhas = aba === 'hoje' ? await listarHoje() : await listarTodos();
      clear(listaBox);
      if (!linhas.length) {
        listaBox.append(el('p', { class: 'view__sub', text: aba === 'hoje' ? 'Nenhum crachá esquecido registrado hoje.' : 'Nenhum registro de crachá esquecido.' }));
        return;
      }
      if (aba === 'todos' && linhas.length >= TETO_TODOS) {
        listaBox.append(el('p', { class: 'view__sub', text: `Mostrando os ${TETO_TODOS} registros mais recentes — use a busca abaixo para filtrar.` }));
      }
      renderTabela(listaBox, {
        colunas: colunas(aba === 'todos'),
        linhas, buscarEm: ['nome', 'cpf', 'matricula', 'operador'], porPagina: 12,
      });
    }

    desenhar(true);
    assinarTabela('cracha_esquecido', () => desenhar());
    pollPeriodico(() => desenhar());
  },
};